import { useState, useEffect } from 'react';
import { UserProfile, Gender, CharacterClass, INTERESTS, GENDER_OPTIONS, INTERESTED_IN_OPTIONS, normalizeGender } from '../types/user';
import { GamerStatus, GAMER_STATUS_OPTIONS, parseAvailabilityWindows, formatAvailabilitySummary } from '../types/profile';
import { profileService } from '../services/api';
import { useUser } from '../context/UserContext';
import { CharacterEditorInline } from './CharacterEditor/CharacterEditorInline';
import { DatingProfileEdit } from './profile';
import { NpcSectionHeader } from './profile/NpcSectionHeader';
import { WorldKnowsRing } from './profile/WorldKnowsRing';
import { InterestPillGrid } from './profile/InterestPillGrid';
import { npcHostFor } from '../data/npcSectionHosts';
import { MemoriesJournal } from './memories/MemoriesJournal';
import './ProfilePage.css';

type ProfileTab = 'account' | 'personal' | 'dating' | 'game' | 'memories';

interface ProfilePageProps {
  onClose: () => void;
}

export function ProfilePage({ onClose }: ProfilePageProps) {
  const { user, updateProfile } = useUser();
  const [activeTab, setActiveTab] = useState<ProfileTab>('personal');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // "How well the world knows you" ring — fed by GET /api/profile/completion for
  // now (forward-compatible with /api/profile/traits). Degrades to 0% on failure.
  const [worldKnowsPercent, setWorldKnowsPercent] = useState(0);

  // Personal tab form data
  const [personalData, setPersonalData] = useState({
    displayName: user?.displayName || '',
    bio: user?.bio || '',
    age: user?.age || 25,
    gender: user?.gender ? normalizeGender(user.gender) : 'man' as Gender,
    location: user?.location || '',
    lookingFor: user?.lookingFor?.map(normalizeGender) || ['woman', 'man'] as Gender[],
    interests: user?.interests || [] as string[],
    minAge: user?.preferences.minAge || 18,
    maxAge: user?.preferences.maxAge || 45,
  });

  // Game tab form data
  const [gameData, setGameData] = useState({
    characterClass: user?.characterClass || 'tank' as CharacterClass,
  });

  // In-game questions (stored on the dating profile)
  const [gameAnswers, setGameAnswers] = useState<{
    isGamer?: GamerStatus;
    gameLookingForPartners?: boolean;
  }>({});

  // Availability windows (V2 Amethyst WU-02, R2) — a read-only display chip
  // here; the actual picker lives in the Dating tab (DatingProfileEdit),
  // which owns the rich profile this field belongs to.
  const [availabilitySummary, setAvailabilitySummary] = useState<string | null>(null);

  // "My Character" card is small and collapsed by default (V2 Amethyst
  // WU-02, R1) — tap to expand into the full builder, never shown big by
  // default the way the old two-pane layout did.
  const [isCharacterExpanded, setIsCharacterExpanded] = useState(false);

  // Track if there are unsaved changes
  const [hasPersonalChanges, setHasPersonalChanges] = useState(false);
  const [hasGameChanges, setHasGameChanges] = useState(false);

  // Load in-game answers + availability once from the rich dating profile
  useEffect(() => {
    let cancelled = false;
    profileService.getProfile().then(profile => {
      if (!cancelled && profile) {
        setGameAnswers({
          isGamer: profile.isGamer,
          gameLookingForPartners: profile.gameLookingForPartners,
        });
        setAvailabilitySummary(formatAvailabilitySummary(parseAvailabilityWindows(profile.availabilityWindowsJson)));
      }
    }).catch(() => { /* profile may not exist yet — questions start blank */ });
    return () => { cancelled = true; };
  }, []);

  // Load the completion percentage that feeds the WorldKnowsRing.
  useEffect(() => {
    let cancelled = false;
    profileService.getCompletionStatus().then(status => {
      if (!cancelled && status) {
        setWorldKnowsPercent(status.completionPercentage ?? 0);
      }
    }).catch(() => { /* auth/token issue — ring degrades to 0% (never throws) */ });
    return () => { cancelled = true; };
  }, []);

  const handleGameAnswer = (field: 'isGamer' | 'gameLookingForPartners', value: GamerStatus | boolean) => {
    setGameAnswers(prev => ({ ...prev, [field]: value }));
    setHasGameChanges(true);
    setSaveMessage(null);
  };

  useEffect(() => {
    if (user) {
      setPersonalData({
        displayName: user.displayName || '',
        bio: user.bio || '',
        age: user.age || 25,
        gender: user.gender ? normalizeGender(user.gender) : 'man',
        location: user.location || '',
        lookingFor: user.lookingFor?.map(normalizeGender) || ['woman', 'man'],
        interests: user.interests || [],
        minAge: user.preferences.minAge || 18,
        maxAge: user.preferences.maxAge || 45,
      });
      setGameData({
        characterClass: user.characterClass || 'tank',
      });
    }
  }, [user]);

  const handlePersonalChange = (field: string, value: string | number | Gender | Gender[] | string[]) => {
    setPersonalData(prev => ({ ...prev, [field]: value }));
    setHasPersonalChanges(true);
    setSaveMessage(null);
  };

  // Note: handleGameChange removed since character class selection was removed

  const toggleInterest = (interest: string) => {
    setPersonalData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
    setHasPersonalChanges(true);
    setSaveMessage(null);
  };

  const toggleLookingFor = (gender: Gender) => {
    setPersonalData(prev => ({
      ...prev,
      lookingFor: prev.lookingFor.includes(gender)
        ? prev.lookingFor.filter(g => g !== gender)
        : [...prev.lookingFor, gender]
    }));
    setHasPersonalChanges(true);
    setSaveMessage(null);
  };

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    setSaveMessage(null);

    try {
      const updatedProfile: UserProfile = {
        ...user,
        displayName: personalData.displayName,
        bio: personalData.bio,
        age: personalData.age,
        gender: personalData.gender,
        location: personalData.location,
        lookingFor: personalData.lookingFor,
        interests: personalData.interests,
        characterClass: gameData.characterClass,
        preferences: {
          ...user.preferences,
          minAge: personalData.minAge,
          maxAge: personalData.maxAge,
          interests: personalData.interests,
        },
        lastActive: new Date(),
      };

      await updateProfile(updatedProfile);
      if (hasGameChanges) {
        await profileService.updateProfile(gameAnswers);
      }
      setHasPersonalChanges(false);
      setHasGameChanges(false);
      setSaveMessage({ type: 'success', text: 'Profile saved successfully!' });
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Failed to save profile. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Unknown';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const hasUnsavedChanges = hasPersonalChanges || hasGameChanges;

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-page-card">
          <p>Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-page-card">
        <div className="profile-header">
          <h1>My Profile</h1>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="profile-tabs">
          <button
            className={`tab-btn ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            Account
          </button>
          <button
            className={`tab-btn ${activeTab === 'personal' ? 'active' : ''} ${hasPersonalChanges ? 'has-changes' : ''}`}
            onClick={() => setActiveTab('personal')}
          >
            Personal
            {hasPersonalChanges && <span className="unsaved-dot"></span>}
          </button>
          <button
            className={`tab-btn ${activeTab === 'dating' ? 'active' : ''}`}
            onClick={() => setActiveTab('dating')}
          >
            Dating
          </button>
          <button
            className={`tab-btn ${activeTab === 'game' ? 'active' : ''} ${hasGameChanges ? 'has-changes' : ''}`}
            onClick={() => setActiveTab('game')}
          >
            Game
            {hasGameChanges && <span className="unsaved-dot"></span>}
          </button>
          <button
            className={`tab-btn ${activeTab === 'memories' ? 'active' : ''}`}
            onClick={() => setActiveTab('memories')}
          >
            Memories
          </button>
        </div>

        {/* Tab Content */}
        <div className="profile-content">
          {/* Account Tab - Read Only */}
          {activeTab === 'account' && (
            <div className="tab-content account-tab">
              <p className="tab-description">Your account information is managed by the system and cannot be changed here.</p>

              <div className="info-group">
                <label>Username</label>
                <div className="info-value">
                  <span className="readonly-badge">Read-only</span>
                  {user.username}
                </div>
              </div>

              <div className="info-group">
                <label>Email</label>
                <div className="info-value">
                  <span className="readonly-badge">Read-only</span>
                  {user.email}
                </div>
              </div>

              <div className="info-group">
                <label>User ID</label>
                <div className="info-value info-id">
                  {user.id}
                </div>
              </div>

              <div className="info-row">
                <div className="info-group">
                  <label>Member Since</label>
                  <div className="info-value">
                    {formatDate(user.createdAt)}
                  </div>
                </div>
                <div className="info-group">
                  <label>Last Active</label>
                  <div className="info-value">
                    {formatDate(user.lastActive)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Personal Tab — V2 Amethyst WU-02 (R1) person-first order:
              identity → interests → looking-for/availability → about →
              "My character" (small, secondary, collapsed by default). */}
          {activeTab === 'personal' && (
            <div className="tab-content personal-tab personal-shell">
              <div className="personal-know-block">
                <WorldKnowsRing percent={worldKnowsPercent} />
              </div>
              <section className="form-section">
                <NpcSectionHeader host={npcHostFor('basics')} />
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={personalData.displayName}
                    onChange={(e) => handlePersonalChange('displayName', e.target.value)}
                    placeholder="How others will see you"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Age</label>
                    <input
                      type="number"
                      value={personalData.age}
                      onChange={(e) => handlePersonalChange('age', parseInt(e.target.value) || 18)}
                      min={18}
                      max={99}
                    />
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={personalData.location}
                      onChange={(e) => handlePersonalChange('location', e.target.value)}
                      placeholder="City, Country"
                    />
                  </div>
                </div>
              </section>

              <section className="form-section">
                <NpcSectionHeader host={npcHostFor('interests')} />
                <p className="field-hint">Pick a few that describe you</p>
                <InterestPillGrid
                  interests={INTERESTS}
                  selected={personalData.interests}
                  onToggle={toggleInterest}
                />
              </section>

              <section className="form-section">
                <NpcSectionHeader host={npcHostFor('meet')} />
                <div className="form-group">
                  <label>I am</label>
                  <div className="gender-options">
                    {GENDER_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={`option-btn gender-btn ${personalData.gender === option.value ? 'selected' : ''}`}
                        onClick={() => handlePersonalChange('gender', option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>I'm interested in</label>
                  <p className="field-hint">Select all that apply</p>
                  <div className="gender-options">
                    {INTERESTED_IN_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={`option-btn gender-btn ${personalData.lookingFor.includes(option.value) ? 'selected' : ''}`}
                        onClick={() => toggleLookingFor(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Age range</label>
                  <div className="age-range">
                    <input
                      type="number"
                      value={personalData.minAge}
                      onChange={(e) => handlePersonalChange('minAge', parseInt(e.target.value) || 18)}
                      min={18}
                      max={personalData.maxAge}
                    />
                    <span>to</span>
                    <input
                      type="number"
                      value={personalData.maxAge}
                      onChange={(e) => handlePersonalChange('maxAge', parseInt(e.target.value) || 99)}
                      min={personalData.minAge}
                      max={99}
                    />
                  </div>
                </div>

                {/* Availability display chip (V2 Amethyst WU-02, R2) —
                    read-only here; edited in Dating → Lifestyle. */}
                <div className="form-group">
                  <label>Availability</label>
                  <p className="availability-display-chip">
                    {availabilitySummary ?? 'Not set yet'}
                    <button
                      type="button"
                      className="availability-edit-link"
                      onClick={() => setActiveTab('dating')}
                    >
                      Edit in Dating tab
                    </button>
                  </p>
                </div>
              </section>

              <section className="form-section">
                <NpcSectionHeader host={npcHostFor('about')} />
                <div className="form-group">
                  <textarea
                    value={personalData.bio}
                    onChange={(e) => handlePersonalChange('bio', e.target.value)}
                    placeholder="Tell others about yourself..."
                    rows={4}
                  />
                </div>
              </section>

              {/* "My Character" — small, secondary, collapsed by default
                  (V2 Amethyst WU-02, R1: "tap → full/builder", not a
                  prominent sticky viewport next to the form). */}
              <section className="form-section my-character-card">
                <button
                  type="button"
                  className="my-character-toggle"
                  onClick={() => setIsCharacterExpanded((v) => !v)}
                  aria-expanded={isCharacterExpanded}
                >
                  <span className="my-character-toggle-icon">🎮</span>
                  <span className="my-character-toggle-text">
                    <strong>My character</strong>
                    <small>The costume you wear in rooms — tap to customize</small>
                  </span>
                  <span className={`my-character-chevron ${isCharacterExpanded ? 'is-open' : ''}`}>▾</span>
                </button>
                {isCharacterExpanded && (
                  <div className="my-character-editor">
                    <CharacterEditorInline />
                  </div>
                )}
              </section>
            </div>
          )}

          {/* Dating Profile Tab — the rich profile: photos, prompts, lifestyle,
              and per-field visibility (what's public / connections-only / private) */}
          {activeTab === 'dating' && (
            <DatingProfileEdit onClose={() => setActiveTab('personal')} />
          )}

          {/* Game Tab - Character Editor */}
          {activeTab === 'game' && (
            <div className="tab-content game-tab">
              <div className="gaming-optional-note">
                <span className="note-icon">💡</span>
                <span className="note-text">
                  <strong>Gaming is optional!</strong> Characters are only used in game rooms.
                  You can connect with people in dating rooms without any gaming setup.
                </span>
              </div>
{/* 
              <div className="section">
                <h3>Default Character Class</h3>
                <p className="section-hint">This class will be used when you join games</p>
                <div className="character-grid">
                  {CHARACTER_CLASSES.map(charClass => (
                    <button
                      key={charClass.value}
                      type="button"
                      className={`character-card ${gameData.characterClass === charClass.value ? 'selected' : ''}`}
                      onClick={() => handleGameChange('characterClass', charClass.value)}
                    >
                      <div className="character-icon">
                        {charClass.value === 'tank' && '🛡️'}
                        {charClass.value === 'archer' && '🏹'}
                        {charClass.value === 'mage' && '🔮'}
                        {charClass.value === 'rogue' && '🗡️'}
                      </div>
                      <div className="character-name">{charClass.label}</div>
                      <div className="character-desc">{charClass.description}</div>
                    </button>
                  ))}
                </div>
                <div className="character-preview-note">
                  Your character will appear as: <strong>{genderToCharacterSuffix(personalData.gender)} {gameData.characterClass.charAt(0).toUpperCase() + gameData.characterClass.slice(1)}</strong>
                </div>
              </div> */}

              <section className="form-section">
                <h3 className="form-section-title">In-game questions</h3>
                <div className="form-group">
                  <label>Are you a gamer?</label>
                  <div className="gender-options">
                    {GAMER_STATUS_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={`option-btn ${gameAnswers.isGamer === option.value ? 'selected' : ''}`}
                        onClick={() => handleGameAnswer('isGamer', option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Looking for a gaming partner?</label>
                  <div className="gender-options">
                    <button
                      type="button"
                      className={`option-btn ${gameAnswers.gameLookingForPartners === true ? 'selected' : ''}`}
                      onClick={() => handleGameAnswer('gameLookingForPartners', true)}
                    >
                      Yes, let's game together!
                    </button>
                    <button
                      type="button"
                      className={`option-btn ${gameAnswers.gameLookingForPartners === false ? 'selected' : ''}`}
                      onClick={() => handleGameAnswer('gameLookingForPartners', false)}
                    >
                      Just sharing my interests
                    </button>
                  </div>
                </div>
              </section>

              <div className="section">
                <h3>My Characters</h3>
                <p className="section-hint">Create and customize your game characters</p>
                <CharacterEditorInline />
              </div>
            </div>
          )}

          {/* Memories Tab — the remembrance journal. W1 plants the mount anchor;
              WU-W3 fills it (C-4). Do not add memory UI here. */}
          {activeTab === 'memories' && (
            <div className="tab-content memories-tab">
              <MemoriesJournal />
            </div>
          )}
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={`save-message ${saveMessage.type}`}>
            {saveMessage.text}
          </div>
        )}

        {/* Actions */}
        <div className="profile-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
          >
            {hasUnsavedChanges ? 'Cancel' : 'Close'}
          </button>
          {(activeTab === 'personal' || activeTab === 'game') && (
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={!hasUnsavedChanges || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
